import {matchPath} from 'react-router'

import {AttendanceInputSuggestion} from '../attendance/attendanceInputSuggestions'

/**
 * The Da1Timetable (selective AI) help bot's screens: which screen a route is, sent as
 * `options.pageContext.page.id` so the answer is worded for it, and the starter chips its welcome
 * offers there.
 *
 * The ids are the contract with node-api (`components/chatbot/aiHelp/screens.js`); the route
 * patterns are the portal's own (`SchoolRoutes.tsx`, `SchedulePage.tsx`, `UserManagementPage.tsx`).
 * The chips are the user manual's own section titles, sent as the message exactly as written: the
 * manual is English-only, and the backend finds the section by its title, so they are not
 * translated. AI credits have no route of their own (a header modal), so `credits` is never sent.
 */
type AiHelpScreen = {
  id: string
  /** react-router patterns; a trailing `/*` also matches the route itself. First match wins. */
  paths: string[]
  chips: string[]
}

const TIMETABLE = '/institute/schedule/timetable'

const AI_HELP_SCREENS: AiHelpScreen[] = [
  {
    id: 'dashboard',
    paths: ['/dashboard'],
    chips: [
      'Find your way around the screen',
      'What admins, staff and teachers can see',
      'Use the Dashboard',
    ],
  },
  {
    id: 'timetable.new_version',
    paths: [`${TIMETABLE}/new-version/*`],
    chips: [
      'Generate a timetable',
      'Final Check Before Generation',
      'How many AI credits does generation use?',
    ],
  },
  {
    id: 'timetable.constraints',
    // Any `?step=`: the query string is never part of the pathname.
    paths: [`${TIMETABLE}/constraints/*`],
    chips: ['Set institution hours for each day', 'Mark off days', 'Add a break'],
  },
  {
    id: 'timetable.visualization',
    paths: [`${TIMETABLE}/visualization/*`],
    chips: [
      'Preview and adjust the slot structure',
      'Change, add or delete a slot or break',
      'Short slots (Reduced-Duration Slots)',
    ],
  },
  {
    id: 'timetable.workload_breakdown',
    paths: [`${TIMETABLE}/workload-breakdown/*`],
    chips: [
      'Set how often and how long each subject is taught',
      'Change frequency, teacher or venue for many lessons',
      'Pair or run two subjects at the same time',
    ],
  },
  {
    id: 'timetable.teacher_workload',
    paths: [`${TIMETABLE}/teacher-workload/*`],
    chips: ['Read the Teacher Workload page'],
  },
  {
    id: 'timetable.view',
    paths: [`${TIMETABLE}/view/*`],
    chips: [
      "What's on the Timetable page?",
      'Switch between class, teacher and venue views',
      'Filter and arrange the grid',
    ],
  },
  {
    id: 'timetable.picker',
    paths: [TIMETABLE],
    chips: [
      'Who can generate, edit and export timetables?',
      'Open the Create New Timetable Version page',
      'Open a timetable',
    ],
  },
  {id: 'data.students', paths: ['/institute/student/*'], chips: ['Add a student']},
  {
    id: 'data.teachers',
    paths: ['/institute/teacher/*'],
    chips: ['What is in the Data menu?', 'Add a teacher', 'Edit or delete teachers'],
  },
  {
    id: 'data.grades',
    paths: ['/institute/grades/*'],
    chips: ['Add a grade', "Why can't I delete a grade or section?"],
  },
  {id: 'data.sections', paths: ['/institute/sections/*'], chips: ['Add a section']},
  {
    id: 'data.classes',
    paths: ['/institute/classes/*'],
    chips: [
      'Add a class',
      'Remove a class that still has students',
      'Edit or delete many rows at once',
    ],
  },
  {
    id: 'data.subjects',
    paths: ['/institute/subjects/*'],
    chips: ['Add a subject and assign its teacher'],
  },
  {
    id: 'data.excel_import',
    paths: ['/institute/data-import/csv-import/*'],
    chips: [
      'Which Excel template should I use?',
      'Import data from Excel',
      'Tips for a clean Excel file',
    ],
  },
  {
    id: 'support.tickets',
    paths: ['/institute/support-tickets/*'],
    chips: ['Raise a support ticket', 'Track your support tickets'],
  },
  {
    id: 'profile',
    paths: ['/profile/*'],
    chips: ['Edit your profile', 'Change your password', 'Sign out'],
  },
  {
    id: 'users',
    // User Management > Users on these domains; the classic staff list too.
    paths: ['/institute/user-management/users/*', '/institute/staff/*'],
    chips: ['How users and roles work', 'Add a user'],
  },
  {
    id: 'roles_permissions',
    paths: ['/institute/user-management/roles-permissions/*'],
    chips: ['Create a role', 'What each permission allows', 'Edit or delete a role'],
  },
]

const screenFor = (pathname: string): AiHelpScreen | null =>
  AI_HELP_SCREENS.find((screen) =>
    screen.paths.some((pattern) => matchPath({path: pattern, end: true}, pathname) !== null)
  ) ?? null

/** The help bot's screen id for this route (`timetable.constraints`, …), or null when unmapped. */
export const aiHelpScreenId = (pathname: string): string | null => screenFor(pathname)?.id ?? null

/**
 * The welcome's starter chips for this route: its screen's, else the Dashboard's. Each chip's
 * text is the message it sends.
 */
export function getAiHelpWelcomeSuggestions(pathname: string): AttendanceInputSuggestion[] {
  const screen = screenFor(pathname) ?? AI_HELP_SCREENS[0]
  return screen.chips.map((chip, index) => ({
    id: `ai-help-${screen.id}-${index}`,
    label: chip,
    text: chip,
    icon: 'support',
  }))
}
